import { css } from "../helpers/webComponents";

/** CSS for the CCTV Analyzer component */
const styles: string = css`
    :host {
        display: block;
        font-family: "Onesize", monospace;
        padding: 20px;
        background-color: #2a2a2a;
        border: 3px solid #7f6ed7;
        border-radius: 5px;
        max-width: 600px;
        min-width: 400px;
        margin: 0 auto;
        color: #fff;
        box-shadow: 0 0 20px rgba(127, 110, 215, 0.3);
    }

    .analyzer-container {
        width: 100%;
    }

    .title {
        text-align: center;
        font-size: 1.5em;
        margin-bottom: 20px;
        color: #7f6ed7;
        text-shadow: 0 0 5px rgba(127, 110, 215, 0.5);
    }

    .console {
        background-color: #1c1c1c;
        border: 1px solid #555;
        padding: 15px;
        margin-bottom: 20px;
        font-family: monospace;
        height: 150px;
        overflow-y: auto;
        color: #0f0;
        box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.5);
    }

    .console p {
        margin: 0;
        padding: 3px 0;
    }

    .blinking-cursor {
        animation: blink 1s step-end infinite;
    }

    @keyframes blink {
        50% {
            opacity: 0;
        }
    }

    .button {
        background-color: #7f6ed7;
        border: 2px solid #332c57;
        border-radius: 4px;
        padding: 12px 24px;
        margin: 0 auto;
        text-transform: uppercase;
        cursor: pointer;
        display: block;
        text-align: center;
        user-select: none;
        font-weight: bold;
        color: white;
        font-size: 16px;
        transition: all 0.2s ease;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }

    .button:hover {
        background-color: #9a8ae8;
        transform: translateY(-2px);
        box-shadow: 0 6px 8px rgba(0, 0, 0, 0.2);
    }

    .button:disabled {
        background-color: #555;
        cursor: not-allowed;
        transform: none;
        box-shadow: none;
    }
`;

interface ResponseData {
    message: string;
    success: boolean;
}

/**
 * Web component voor de CCTV footage analyzer
 */
export class CctvAnalyzer extends HTMLElement {
    private consoleElement: HTMLElement | null = null;
    private buttonElement: HTMLElement | null = null;
    private restoring: boolean = false;
    private completed: boolean = false;

    /**
     * de "constructor" van de Web Component
     */
    public connectedCallback(): void {
        console.log("CctvAnalyzer connected to DOM");
        this.attachShadow({ mode: "open" });
        this.render();
        this.initEventListeners();
    }

    /**
     * Initialize event listeners
     */
    private initEventListeners(): void {
        console.log("CctvAnalyzer initializing event listeners");
        if (this.shadowRoot) {
            this.buttonElement = this.shadowRoot.querySelector(".button");
            this.consoleElement = this.shadowRoot.querySelector(".console");

            if (this.buttonElement) {
                this.buttonElement.addEventListener("click", () => this.handleRestoreClick());
                console.log("CctvAnalyzer button event listener added");
            }
            else {
                console.error("CctvAnalyzer button element not found in shadowRoot");
            }
        }
        else {
            console.error("CctvAnalyzer shadowRoot not available for event listeners");
        }
    }

    /**
     * Handle the click event for restoring footage
     */
    private async handleRestoreClick(): Promise<void> {
        console.log("CctvAnalyzer restore button clicked");
        if (this.restoring || this.completed || !this.buttonElement || !this.consoleElement) {
            return;
        }

        this.restoring = true;
        this.buttonElement.setAttribute("disabled", "true");
        this.buttonElement.textContent = "Processing...";

        await this.addConsoleMessage("Scanning deleted files...");
        await this.delay(800);
        await this.addConsoleMessage("Found 3 corrupted CCTV files from the night of the murder.");
        await this.delay(1000);
        await this.addConsoleMessage("Attempting recovery sequence...");
        await this.delay(1500);

        try {
            console.log("CctvAnalyzer making API request to restore footage");
            const apiUrl: string = VITE_API_URL;
            console.log("Using API URL:", apiUrl);
            const response: Response = await fetch(`${apiUrl}game/restore-footage`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-PlayerSessionId": localStorage.getItem("playerSessionId") || "",
                },
            });

            if (!response.ok) {
                throw new Error(`Server returned ${response.status}: ${response.statusText}`);
            }

            let data: ResponseData;
            try {
                data = await response.json() as ResponseData;
            }
            catch (jsonError) {
                console.error("Failed to parse JSON response:", jsonError);
                throw new Error("Failed to parse server response");
            }

            await this.addConsoleMessage(data.message);
            console.log("CctvAnalyzer API response:", data);

            this.completed = true;
            this.buttonElement.textContent = "Recovery Complete";
        }
        catch (error: unknown) {
            console.error("CctvAnalyzer API error:", error);
            const errorMsg: string = error instanceof Error ? error.message : "Unknown error";
            await this.addConsoleMessage(`Error: Unable to restore footage. (${errorMsg})`);
            this.buttonElement.removeAttribute("disabled");
            this.buttonElement.textContent = "Retry Restore";
        }

        this.restoring = false;
    }

    /**
     * bericht toeveogen aan de console met een typ effect
     */
    private async addConsoleMessage(message: string): Promise<void> {
        if (!this.consoleElement) {
            console.error("CctvAnalyzer console element not available");
            return;
        }

        const messageElement: HTMLParagraphElement = document.createElement("p");
        messageElement.innerHTML = "";
        this.consoleElement.appendChild(messageElement);

        // elk karakter toevoegen met een vertraging voor typingeffect
        for (let i: number = 0; i < message.length; i++) {
            messageElement.innerHTML = message.substring(0, i + 1) + "<span class=\"blinking-cursor\">_</span>";
            await this.delay(20);
        }

        // de cursor verwijderen nadat het typen klaar is
        messageElement.innerHTML = message;

        // Scroll naar beneden
        this.consoleElement.scrollTop = this.consoleElement.scrollHeight;
    }

    /**
     * Wacht een aantal milliseconden - helper functie dus
     */
    private delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Render de component
     */
    private render(): void {
        if (!this.shadowRoot) {
            console.error("CctvAnalyzer shadowRoot not available for rendering");
            return;
        }

        console.log("CctvAnalyzer rendering component");

        // Verwijder bestaande inhoud
        while (this.shadowRoot.firstChild) {
            this.shadowRoot.removeChild(this.shadowRoot.firstChild);
        }

        // Maak een stijlelement
        const styleElement: HTMLStyleElement = document.createElement("style");
        styleElement.textContent = styles;
        this.shadowRoot.appendChild(styleElement);

        // maak container
        const container: HTMLDivElement = document.createElement("div");
        container.className = "analyzer-container";
        this.shadowRoot.appendChild(container);

        // title
        const title: HTMLDivElement = document.createElement("div");
        title.className = "title";
        title.textContent = "CCTV Footage Recovery Tool";
        container.appendChild(title);

        // console
        this.consoleElement = document.createElement("div");
        this.consoleElement.className = "console";

        // initial messages toevoegen
        const initialMessage1: HTMLParagraphElement = document.createElement("p");
        initialMessage1.textContent = "CCTV-RECOVERY v1.0.1";
        this.consoleElement.appendChild(initialMessage1);

        const initialMessage2: HTMLParagraphElement = document.createElement("p");
        initialMessage2.textContent = "Scanning for deleted footage...";
        this.consoleElement.appendChild(initialMessage2);
        container.appendChild(this.consoleElement);

        // button
        this.buttonElement = document.createElement("div");
        this.buttonElement.className = "button";
        this.buttonElement.textContent = "Restore Video Files";
        container.appendChild(this.buttonElement);

        console.log("CctvAnalyzer render complete", {
            shadowRoot: this.shadowRoot,
            button: this.buttonElement,
            console: this.consoleElement,
        });
    }
}
